/**
 * Learning Content Prompts Library
 * 
 * Age-specific story prompts following Montessori pedagogy for bilingual learning.
 * Organized by age categories: 0-1, 1-2, 3-4, 5-6, 7-8, 9-10
 */

export type AgeCategory = '0-1' | '1-2' | '3-4' | '5-6' | '7-8' | '9-10';

export type LearningTopic =
  | 'practical-life'
  | 'sensorial'
  | 'language'
  | 'cultural'
  | 'nature'
  | 'science'
  | 'mathematics'
  | 'geography'
  | 'history'
  | 'art'
  | 'music'
  | 'social-emotional'
  | 'movement';

export type SensitivePeriod =
  | 'language'
  | 'order'
  | 'movement'
  | 'sensorial'
  | 'social'
  | 'refinement-of-senses'
  | 'small-objects';

export interface LearningPrompt {
  title: string;
  prompt: string;
  ageCategory: AgeCategory;
  difficultyLevel: number; // 1-5
  learningTopic: LearningTopic;
  pageCount: number;
  learningObjectives: string[];
  montessoriPrinciples: string[];
  sensitivePeriods: SensitivePeriod[];
  parentGuidance: string;
  followUpActivities: string[];
}

/**
 * AGE 0-1: INFANTS
 * Focus: Sensory exposure, single words, bonding, routine
 * Montessori: Absorbent mind, language sensitive period begins, sensorial exploration
 */
export const AGE_0_1_PROMPTS: LearningPrompt[] = [
  {
    title: 'First Words: Mama & Papa',
    prompt: 'Create a gentle infant picture book featuring clear, close-up photographs of a mother\'s face (Mama) and father\'s face (Papa). Each page shows one loving parent with a warm smile. Use high-contrast, simple compositions perfect for infant vision. Include gentle narration like "This is Mama. Mama loves you." Text should be minimal - just the words "Mama" and "Papa" with simple loving phrases.',
    ageCategory: '0-1',
    difficultyLevel: 1,
    learningTopic: 'language',
    pageCount: 4,
    learningObjectives: [
      'Introduce first words Mama/Papa in both languages',
      'Support face recognition and bonding',
      'Develop early language comprehension through repetition'
    ],
    montessoriPrinciples: [
      'Absorbent mind - infants soak up language naturally',
      'Sensitive period for language begins at birth',
      'Real photographs over illustrations for authenticity'
    ],
    sensitivePeriods: ['language', 'sensorial'],
    parentGuidance: 'Read this book during quiet bonding time. Point to your own face when saying "Mama" or "Papa". Let baby look at the faces. Repetition is key - read multiple times per day. This supports face recognition and early word association.',
    followUpActivities: [
      'Point to family photos and name people',
      'Play peek-a-boo while saying "Mama" and "Papa"',
      'Have different family members hold baby while saying their name'
    ]
  },
  {
    title: 'Colorful World: Red, Blue, Yellow',
    prompt: 'Create a sensorial book for infants featuring bold, single-color pages. Each page shows one primary color (red, blue, yellow) with a simple real object in that color. For example: a red apple, a blue ball, a yellow banana. Use high-contrast photography. Text: Just the color name and object name. "Red. Apple." Keep it minimal and clear.',
    ageCategory: '0-1',
    difficultyLevel: 1,
    learningTopic: 'sensorial',
    pageCount: 6,
    learningObjectives: [
      'Introduce primary colors in both languages',
      'Support visual development and color recognition',
      'Associate colors with real objects',
      'Build basic vocabulary (color names, simple nouns)'
    ],
    montessoriPrinciples: [
      'Sensorial education begins in infancy',
      'Real objects and clear colors support learning',
      'One concept at a time (color + object)'
    ],
    sensitivePeriods: ['sensorial', 'language'],
    parentGuidance: 'Show baby the book while saying color names in both languages. Point to real objects in your environment that match the colors. Let baby touch textured pages if possible. Make it a sensory experience.',
    followUpActivities: [
      'Point out colors during daily routines',
      'Let baby hold safe colored objects',
      'Create a color treasure basket with red, blue, and yellow items'
    ]
  },
  {
    title: 'Sounds Around Us',
    prompt: 'Create a sound-focused book for infants. Each page features one clear sound and image: a ringing bell, a chirping bird, running water, a meowing cat. Include simple onomatopoeia. "Ring ring! Bell." "Chirp chirp! Bird." Use clear, realistic images. Audio should include the actual sound followed by the word in both languages.',
    ageCategory: '0-1',
    difficultyLevel: 1,
    learningTopic: 'sensorial',
    pageCount: 5,
    learningObjectives: [
      'Develop auditory discrimination',
      'Connect sounds to sources',
      'Learn sound words and nouns in two languages',
      'Support listening skills'
    ],
    montessoriPrinciples: [
      'Sensorial refinement through sound exploration',
      'Real sounds connect to real experiences',
      'Naming objects precisely builds vocabulary'
    ],
    sensitivePeriods: ['sensorial', 'language'],
    parentGuidance: 'Play the sounds and let baby listen. Pause between sounds. Make the sounds yourself (ring a bell, chirp). Connect to real-life: when baby hears a bird outside, say "Bird! Chirp chirp!" This builds sound association.',
    followUpActivities: [
      'Play with rattles, bells, and sound-making toys',
      'Take sound walks - listen for birds, cars, dogs',
      'Create a sound basket with safe objects that make different noises'
    ]
  },
  {
    title: 'Good Morning Routine',
    prompt: 'Create a gentle morning routine book for infants. Show a baby waking up, sun rising, stretching, getting a diaper change, drinking milk, and receiving a morning hug. Use soft, realistic illustrations or photos. Text: "Good morning! Sun is up." "Time for milk." "Big hug!" Keep language simple, warm, and routine-based.',
    ageCategory: '0-1',
    difficultyLevel: 1,
    learningTopic: 'practical-life',
    pageCount: 6,
    learningObjectives: [
      'Introduce daily routine vocabulary',
      'Provide order and predictability',
      'Support language for everyday activities',
      'Build security through routine familiarity'
    ],
    montessoriPrinciples: [
      'Sensitive period for order - routines are comforting',
      'Practical life begins with care of self',
      'Language tied to real experiences'
    ],
    sensitivePeriods: ['order', 'language'],
    parentGuidance: 'Read this book as part of your actual morning routine. Let it mirror what you do. Narrate your actions: "Now we stretch!" This helps baby understand and anticipate routines, which builds security and language comprehension.',
    followUpActivities: [
      'Narrate your morning routine as you do it',
      'Sing a good morning song in both languages',
      'Point to the sun and say "Good morning, sun!"'
    ]
  },
  {
    title: 'Body Parts: Nose, Toes, Hands',
    prompt: 'Create a simple body parts book for infants. Each page features one body part of a baby or toddler in a clear, close-up photo: nose, toes, hands, eyes, mouth, ears. Text: Just the body part name, large and clear. "Nose." "Toes." Optionally include a simple phrase: "This is your nose." Keep it direct and clear.',
    ageCategory: '0-1',
    difficultyLevel: 1,
    learningTopic: 'language',
    pageCount: 6,
    learningObjectives: [
      'Learn body part names in both languages',
      'Support self-awareness and body schema',
      'Prepare for interactive games (point to nose, etc.)',
      'Build essential vocabulary'
    ],
    montessoriPrinciples: [
      'Name precisely - exact language for body parts',
      'Real photos help infant identify with images',
      'Self-awareness is foundational'
    ],
    sensitivePeriods: ['language', 'sensorial'],
    parentGuidance: 'While reading, gently touch baby\'s nose when you say "nose," their toes when you say "toes." Make it interactive and gentle. Over time, ask "Where is your nose?" and help them point. This builds body awareness and language.',
    followUpActivities: [
      'Play "Where is your...?" games during bath or diaper changes',
      'Sing "Head, Shoulders, Knees, and Toes" in both languages',
      'Let baby touch their own nose, toes, etc. in a mirror'
    ]
  }
];

/**
 * AGE 1-2: TODDLERS
 * Focus: Action words, daily routines, simple phrases, movement
 * Montessori: Language explosion, movement sensitive period, order, independence
 */
export const AGE_1_2_PROMPTS: LearningPrompt[] = [
  {
    title: 'Getting Dressed',
    prompt: 'Create a practical life story about a toddler getting dressed. Show: picking a shirt, pulling it on, putting on pants, putting on socks, putting on shoes. Use realistic illustrations of a child doing these actions independently. Text in short sentences: "Mia picks her shirt. She pulls it on. Now pants! She is getting dressed!" Emphasize independence and the process.',
    ageCategory: '1-2',
    difficultyLevel: 1,
    learningTopic: 'practical-life',
    pageCount: 8,
    learningObjectives: [
      'Learn clothing vocabulary in both languages',
      'Understand dressing sequence',
      'Encourage independence in self-care',
      'Build action verb vocabulary (pull, put on, pick)'
    ],
    montessoriPrinciples: [
      'Practical life - care of self',
      'Independence: "Help me do it myself"',
      'Sequences support order and planning'
    ],
    sensitivePeriods: ['order', 'movement', 'language'],
    parentGuidance: 'After reading, invite your toddler to try dressing themselves. Lay out clothes in order. Narrate their actions: "You\'re putting on your shirt!" Encourage, don\'t rush. Montessori teaches that the process is more important than the outcome.',
    followUpActivities: [
      'Practice with a dressing doll or teddy bear',
      'Create a dressing station with clothes at toddler height',
      'Use large buttons or zippers for practice'
    ]
  },
  {
    title: 'Let\'s Jump and Clap!',
    prompt: 'Create an interactive movement book for toddlers. Each page shows a child doing one action: jumping, clapping, stomping, spinning, waving, sitting. Text: "Jump! Can you jump?" "Clap! Clap your hands!" Make it an invitation to move. Use joyful, active photos or illustrations. Encourage imitation.',
    ageCategory: '1-2',
    difficultyLevel: 1,
    learningTopic: 'movement',
    pageCount: 6,
    learningObjectives: [
      'Learn action verbs in both languages',
      'Develop gross motor skills through imitation',
      'Connect words to physical movement',
      'Burn energy and build coordination'
    ],
    montessoriPrinciples: [
      'Movement is essential to learning',
      'Language learned through action is retained better',
      'Freedom of movement supports development'
    ],
    sensitivePeriods: ['movement', 'language'],
    parentGuidance: 'Read this book in an open space. Invite your toddler to do the actions with you. Jump together! Clap together! Make it playful. Narrate in both languages. This is kinesthetic language learning - highly effective for toddlers.',
    followUpActivities: [
      'Play "Simon Says" with action words in both languages',
      'Dance to music and name movements',
      'Create an obstacle course with jump, spin, stomp stations'
    ]
  },
  {
    title: 'Snack Time',
    prompt: 'Create a practical life story about a toddler preparing and eating a snack. Show: washing hands, getting a plate, choosing a banana or apple, peeling/biting, eating, cleaning up. Use clear, step-by-step images. Text: "First, wash your hands. Then get a plate. Choose your snack! Mmmm, banana! All done, clean up!" Simple, sequential.',
    ageCategory: '1-2',
    difficultyLevel: 2,
    learningTopic: 'practical-life',
    pageCount: 8,
    learningObjectives: [
      'Learn food vocabulary in both languages',
      'Understand snack time routine and sequence',
      'Encourage independence in eating',
      'Build hygiene awareness (hand washing)'
    ],
    montessoriPrinciples: [
      'Practical life: grace and courtesy',
      'Prepared environment enables independence',
      'Sequences teach order and responsibility'
    ],
    sensitivePeriods: ['order', 'language', 'refinement-of-senses'],
    parentGuidance: 'Set up a snack station where your toddler can help themselves (within safe limits). After reading, guide them through the process: wash hands, choose snack, sit at table, clean up. Narrate in both languages. This builds independence and vocabulary.',
    followUpActivities: [
      'Let toddler peel a banana or open containers',
      'Practice pouring water from a small pitcher',
      'Create a placemat showing snack time steps'
    ]
  },
  {
    title: 'Where is the Ball?',
    prompt: 'Create a simple question-and-answer story for toddlers. Each page asks "Where is the ball?" in a different setting (under table, in box, behind pillow), then the next page reveals: "Here it is!" Show a colorful ball hidden and found. Text alternates: "Where is the ball?" / "Here it is! Under the table!" Make it playful and interactive.',
    ageCategory: '1-2',
    difficultyLevel: 2,
    learningTopic: 'language',
    pageCount: 8,
    learningObjectives: [
      'Learn question phrase "Where is...?" in both languages',
      'Understand prepositions (under, in, behind)',
      'Develop object permanence and search skills',
      'Build interactive language skills'
    ],
    montessoriPrinciples: [
      'Language through interaction and repetition',
      'Concrete experiences with prepositions',
      'Questions stimulate thinking'
    ],
    sensitivePeriods: ['language', 'small-objects'],
    parentGuidance: 'While reading, pause on the "Where is...?" pages and let your toddler point or guess. Celebrate when they find it! After reading, play hide and seek with real objects. Ask "Where is the cup?" in both languages. This makes language functional and fun.',
    followUpActivities: [
      'Play hide and seek with favorite toys',
      'Hide objects around the room and use preposition words',
      'Use a blanket to hide and reveal objects'
    ]
  },
  {
    title: 'Feelings: Happy and Sad',
    prompt: 'Create a social-emotional book about feelings for toddlers. Show a child in different situations: playing (happy face), toy breaks (sad face), gets a hug (happy again). Use clear facial expressions. Text: "Emma is happy when she plays!" "Oh no, her toy broke. She feels sad." "A hug makes her happy again." Keep it simple and relatable.',
    ageCategory: '1-2',
    difficultyLevel: 2,
    learningTopic: 'social-emotional',
    pageCount: 6,
    learningObjectives: [
      'Learn emotion words (happy, sad) in both languages',
      'Recognize facial expressions for emotions',
      'Understand emotions are normal and can change',
      'Build emotional vocabulary for self-expression'
    ],
    montessoriPrinciples: [
      'Grace and courtesy includes naming emotions',
      'Acknowledging feelings supports emotional regulation',
      'Real situations help children relate'
    ],
    sensitivePeriods: ['language', 'social'],
    parentGuidance: 'When reading, point to the faces and name the emotions. Ask "Is she happy or sad?" Relate to real life: "You felt sad when your tower fell, just like Emma." Naming emotions helps toddlers understand and express feelings, reducing tantrums.',
    followUpActivities: [
      'Make faces in the mirror: "This is my happy face!"',
      'Use emotion cards or drawings during daily moments',
      'Validate feelings: "I see you\'re sad. It\'s okay to feel sad."'
    ]
  }
];

/**
 * AGE 3-4: PRESCHOOL
 * Focus: Narratives, nature, cultural awareness, social skills
 * Montessori: Language refinement, imagination, grace & courtesy, sensorial work
 */
export const AGE_3_4_PROMPTS: LearningPrompt[] = [
  {
    title: 'A Nature Walk in the Forest',
    prompt: 'Create a narrative story about two children taking a nature walk with a parent. They see trees, hear birds chirping, feel tree bark, spot a squirrel, find colorful leaves, and smell flowers. Use beautiful nature illustrations. Text describes what they see, hear, touch, smell. "They listen to the birds. Chirp chirp! They touch the rough bark of the oak tree. It feels bumpy!" Make it sensorial and descriptive.',
    ageCategory: '3-4',
    difficultyLevel: 2,
    learningTopic: 'nature',
    pageCount: 10,
    learningObjectives: [
      'Learn nature vocabulary (tree, bark, leaf, squirrel, bird) in both languages',
      'Develop sensory awareness and description',
      'Encourage observation skills',
      'Foster connection to nature'
    ],
    montessoriPrinciples: [
      'Sensorial education through nature',
      'Real experiences over fantasy',
      'Precise naming of plants and animals'
    ],
    sensitivePeriods: ['sensorial', 'language'],
    parentGuidance: 'After reading, take your own nature walk! Encourage your child to use their senses. "What do you hear? What do you see?" Name things in both languages. Collect leaves or acorns to examine at home. This extends the story into real life.',
    followUpActivities: [
      'Create a nature treasure basket with found items',
      'Press leaves between book pages',
      'Draw pictures of what you saw on your walk',
      'Learn tree names and bird species in your area'
    ]
  },
  {
    title: 'Baking Bread with Grandma',
    prompt: 'Create a practical life story about a child and grandmother baking bread together. Show: gathering ingredients (flour, water, yeast), mixing, kneading dough, letting it rise, baking, and finally enjoying warm bread. Use warm, realistic illustrations. Text describes each step: "Grandma measures the flour. Together they mix it. Now it\'s time to knead! Push and fold, push and fold. The dough rises. Soon it will be ready!" Make it step-by-step and inviting.',
    ageCategory: '3-4',
    difficultyLevel: 2,
    learningTopic: 'practical-life',
    pageCount: 12,
    learningObjectives: [
      'Learn baking and ingredient vocabulary in both languages',
      'Understand sequences and process',
      'Appreciate where food comes from',
      'Build intergenerational connection'
    ],
    montessoriPrinciples: [
      'Practical life: food preparation',
      'Sequences teach order, patience, and planning',
      'Real work is meaningful to children'
    ],
    sensitivePeriods: ['order', 'refinement-of-senses', 'language'],
    parentGuidance: 'If possible, bake together after reading! Let your child measure, pour, and knead (even if messy). Narrate what you\'re doing in both languages. The process is the lesson. Montessori values real work - baking bread is authentic and satisfying for children.',
    followUpActivities: [
      'Make simple recipes together: banana bread, muffins, pizza dough',
      'Visit a bakery and watch bread being made',
      'Set up a play dough "bakery" for pretend baking',
      'Learn about wheat and where flour comes from'
    ]
  },
  {
    title: 'Sharing with Friends',
    prompt: 'Create a social-emotional story about sharing and kindness. Two children, Leo and Aisha, are playing. Leo has a toy truck, Aisha wants to play too. At first Leo doesn\'t want to share (show his face), then he remembers to ask nicely and they play together, taking turns. Both feel happy. Text: "Leo has a truck. Aisha wants to play. \'Can I have a turn?\' she asks. Leo thinks... \'Okay! Let\'s play together!\' They take turns. It\'s more fun together!" Emphasize asking, sharing, and joy of cooperation.',
    ageCategory: '3-4',
    difficultyLevel: 2,
    learningTopic: 'social-emotional',
    pageCount: 10,
    learningObjectives: [
      'Learn sharing and asking vocabulary ("Can I...?", "Please", "Thank you")',
      'Understand taking turns',
      'Model conflict resolution',
      'Build empathy and cooperation'
    ],
    montessoriPrinciples: [
      'Grace and courtesy lessons',
      'Peace education through modeling',
      'Community and collaboration are valued'
    ],
    sensitivePeriods: ['social', 'language'],
    parentGuidance: 'Use this story when sharing conflicts arise. Discuss: "How did Leo feel? How did Aisha feel? What did they do?" Role-play asking and sharing. Montessori classrooms practice grace and courtesy explicitly - this story supports that at home.',
    followUpActivities: [
      'Practice asking politely for things at home',
      'Set a turn-taking timer for toys',
      'Role-play sharing scenarios with stuffed animals',
      'Praise moments of sharing: "You shared! That was kind!"'
    ]
  },
  {
    title: 'Colors of the Rainbow',
    prompt: 'Create a sensorial story that explores the seven colors of the rainbow. Each page features one color with beautiful real-world examples: red apples and roses, orange pumpkins and sunsets, yellow sunflowers and bananas, green grass and leaves, blue sky and ocean, indigo twilight sky, violet lavender flowers. Text: "Red is the color of ripe apples and blooming roses." Make it poetic and descriptive.',
    ageCategory: '3-4',
    difficultyLevel: 2,
    learningTopic: 'sensorial',
    pageCount: 8,
    learningObjectives: [
      'Learn all rainbow color names in both languages',
      'Associate colors with real objects',
      'Develop color discrimination skills',
      'Appreciate beauty in nature'
    ],
    montessoriPrinciples: [
      'Sensorial materials - color tablets progression',
      'Real examples ground abstract concepts',
      'Beauty is part of the prepared environment'
    ],
    sensitivePeriods: ['sensorial', 'refinement-of-senses'],
    parentGuidance: 'After reading, go on a "color hunt" in your home or outside. Find things that match each color. You can create a rainbow collage with found items (leaves, petals, fabric scraps). This extends sensorial learning beyond the book.',
    followUpActivities: [
      'Sort objects by color into rainbow order',
      'Paint with all seven rainbow colors',
      'Make a rainbow snack (strawberries, carrots, bananas, etc.)',
      'Look for rainbows after rain and name the colors'
    ]
  },
  {
    title: 'A Day with a Friend Far Away',
    prompt: 'Create a cultural story comparing a day in the life of two children from different countries. For example, Emma in the USA and Yuki in Japan. Show their morning routines, breakfasts (toast vs rice), going to school, playing, and bedtime. Illustrate cultural differences respectfully. Text: "Emma wakes up in New York. She eats toast with jam. Yuki wakes up in Tokyo. He eats rice and miso soup. Both children go to school. Both love to play!" Emphasize similarities and celebrate differences.',
    ageCategory: '3-4',
    difficultyLevel: 3,
    learningTopic: 'cultural',
    pageCount: 12,
    learningObjectives: [
      'Introduce different cultures and countries',
      'Learn basic cultural vocabulary (foods, greetings)',
      'Understand children around the world have similarities and differences',
      'Build global awareness and respect'
    ],
    montessoriPrinciples: [
      'Cultural education begins early',
      'Respect for all people and cultures',
      'Cosmic education: we are all part of one human family'
    ],
    sensitivePeriods: ['language', 'social'],
    parentGuidance: 'Use a globe or map to show where the children live. Point out that children everywhere eat, play, and go to school, even if details differ. This builds empathy and global citizenship. You can try making one of the foods mentioned.',
    followUpActivities: [
      'Find the countries on a globe or map',
      'Learn "hello" and "thank you" in Japanese and English',
      'Try a food from another culture',
      'Read other books about children from different countries'
    ]
  }
];

/**
 * AGE 5-6: KINDERGARTEN
 * Focus: Early reading, science, longer narratives, cultural stories
 * Montessori: Reading/writing explosion, science exploration, cultural curriculum
 */
export const AGE_5_6_PROMPTS: LearningPrompt[] = [
  {
    title: 'The Life Cycle of a Butterfly',
    prompt: 'Create an educational science story about a butterfly\'s metamorphosis. Start with an egg on a leaf, caterpillar hatches and eats, grows bigger, forms a chrysalis, and emerges as a beautiful butterfly. Use scientifically accurate illustrations. Text explains each stage: "First, a tiny egg. A caterpillar hatches! It eats and eats leaves. It grows very big. Then it makes a chrysalis. Inside, magic happens... A butterfly emerges!" Use accurate terms (chrysalis, not cocoon).',
    ageCategory: '5-6',
    difficultyLevel: 2,
    learningTopic: 'science',
    pageCount: 12,
    learningObjectives: [
      'Learn butterfly life cycle stages in both languages',
      'Understand metamorphosis concept',
      'Build science vocabulary (egg, caterpillar, chrysalis, butterfly)',
      'Appreciate nature\'s transformations'
    ],
    montessoriPrinciples: [
      'Science education grounded in observation',
      'Real phenomenon over fantasy',
      'Precise scientific language'
    ],
    sensitivePeriods: ['language', 'refinement-of-senses'],
    parentGuidance: 'If possible, observe real caterpillars or butterflies. Many science kits let you raise butterflies at home. Draw the stages together. Visit a butterfly garden. Connect the story to real life for deeper understanding.',
    followUpActivities: [
      'Raise butterflies from a kit',
      'Draw and label butterfly life cycle stages',
      'Visit a butterfly conservatory',
      'Plant milkweed or other butterfly-attracting flowers'
    ]
  },
  {
    title: 'The Water Cycle Journey',
    prompt: 'Create a science adventure story following a drop of water through the water cycle. Start as a raindrop falling from a cloud, flowing into a stream, river, then ocean. The sun heats the water, it evaporates into vapor, rises to form clouds, and falls as rain again. Use engaging illustrations showing Earth\'s water systems. Text: "I am a raindrop, falling from the sky! I splash into a stream, flowing down to the river, all the way to the big blue ocean. The warm sun heats me up. I evaporate! I float up, up, up as water vapor. I join my friends in a cloud. Soon we will fall as rain again!" Make it a circular journey.',
    ageCategory: '5-6',
    difficultyLevel: 3,
    learningTopic: 'science',
    pageCount: 14,
    learningObjectives: [
      'Understand the water cycle process',
      'Learn key terms (evaporation, condensation, precipitation) in both languages',
      'Appreciate Earth\'s water systems',
      'Grasp concept of cycles and repetition'
    ],
    montessoriPrinciples: [
      'Cosmic education: understanding Earth systems',
      'Science through storytelling',
      'Interconnectedness of natural processes'
    ],
    sensitivePeriods: ['language'],
    parentGuidance: 'After reading, observe water in nature: rain, puddles, steam from a kettle. Explain that it\'s all part of the water cycle. You can demonstrate evaporation by leaving water in a cup and watching it disappear. This makes an abstract concept concrete.',
    followUpActivities: [
      'Observe water evaporating from a dish',
      'Make "rain" by holding a lid over boiling water (adult supervision)',
      'Draw and label the water cycle',
      'Track weather and discuss where rain comes from'
    ]
  },
  {
    title: 'The Lost Kitten',
    prompt: 'Create a heartwarming narrative story about a group of children who find a lost kitten. They work together to help it: give it water, make a cozy bed, and find its owner by making posters. Show teamwork, problem-solving, kindness, and responsibility. Text with dialogue: "Look! A little kitten!" "It looks lost." "Let\'s help it!" They decide on a plan, work together, and reunite the kitten with its grateful owner. Celebrate cooperation and empathy.',
    ageCategory: '5-6',
    difficultyLevel: 2,
    learningTopic: 'social-emotional',
    pageCount: 16,
    learningObjectives: [
      'Understand story structure (problem, solution, resolution)',
      'Learn vocabulary for emotions, cooperation, problem-solving',
      'Model empathy and community care',
      'Build reading comprehension for longer narratives'
    ],
    montessoriPrinciples: [
      'Peace education and community responsibility',
      'Grace and courtesy extended to animals',
      'Practical problem-solving skills'
    ],
    sensitivePeriods: ['social', 'language'],
    parentGuidance: 'Discuss: "What was the problem? How did the children solve it? How would you feel if you lost your pet?" Relate to real situations. You can role-play what to do if you find a lost animal. This builds empathy and critical thinking.',
    followUpActivities: [
      'Create a "lost pet" poster for practice',
      'Visit an animal shelter and learn about caring for animals',
      'Discuss how to be gentle and kind to pets',
      'Write your own story about helping someone'
    ]
  },
  {
    title: 'Celebrations Around the World',
    prompt: 'Create a cultural exploration story showcasing how different cultures celebrate special occasions. Feature 3-4 celebrations: Diwali in India (festival of lights), Chinese New Year (dragon dance, red envelopes), Day of the Dead in Mexico (colorful altars, marigolds), and Hanukkah (menorah, dreidel). Use vibrant, respectful illustrations. Text explains: "In India, families celebrate Diwali with lights and sweets. In China, people welcome the new year with dragon dances..." Emphasize joy, family, and traditions.',
    ageCategory: '5-6',
    difficultyLevel: 3,
    learningTopic: 'cultural',
    pageCount: 14,
    learningObjectives: [
      'Learn about different cultural celebrations',
      'Build cultural vocabulary and traditions',
      'Develop respect and curiosity for diversity',
      'Understand that all cultures have meaningful traditions'
    ],
    montessoriPrinciples: [
      'Cultural education fosters global citizens',
      'Respect for all people and traditions',
      'Cosmic education: humans as one family with rich diversity'
    ],
    sensitivePeriods: ['language', 'social'],
    parentGuidance: 'Locate each country on a map or globe. If possible, celebrate or try one tradition at home (light candles, make a simple craft). Discuss how your family celebrates special days too. This builds cultural awareness and inclusivity.',
    followUpActivities: [
      'Make a Diwali diya lamp or Chinese lantern craft',
      'Try a traditional food from one culture',
      'Learn a greeting or song from another culture',
      'Create a family traditions book'
    ]
  },
  {
    title: 'My Amazing Five Senses',
    prompt: 'Create an interactive sensorial book exploring the five senses. Each sense gets 2-3 pages: Sight (seeing colors, shapes), Hearing (listening to music, birds), Touch (feeling soft fur, rough bark), Taste (sweet honey, sour lemon), Smell (flowers, cookies baking). Use engaging photos and invite child interaction: "What do YOU see? What do YOU hear?" Text explains how each sense helps us explore the world.',
    ageCategory: '5-6',
    difficultyLevel: 2,
    learningTopic: 'sensorial',
    pageCount: 12,
    learningObjectives: [
      'Learn the five senses in both languages',
      'Understand how each sense functions',
      'Build descriptive vocabulary (soft, loud, sweet, sour)',
      'Appreciate sensory experiences'
    ],
    montessoriPrinciples: [
      'Sensorial education is a pillar of Montessori',
      'Children learn through their senses',
      'Refinement of senses supports intelligence'
    ],
    sensitivePeriods: ['sensorial', 'refinement-of-senses', 'language'],
    parentGuidance: 'Turn this book into a sensory exploration! Create "sense stations": sight (colored objects), hearing (instruments), touch (texture cards), taste (safe foods), smell (spices). Let your child explore each sense actively. Narrate in both languages.',
    followUpActivities: [
      'Create a sensory bin with different textures',
      'Do a blindfold taste test',
      'Listen to nature sounds and identify them',
      'Make a "smell bottles" game with cotton balls and scents'
    ]
  }
];

/**
 * AGE 7-8: LOWER ELEMENTARY
 * Focus: Cosmic education, biographies, folktales, deeper science
 * Montessori: Imagination, research, going out, social learning
 */
export const AGE_7_8_PROMPTS: LearningPrompt[] = [
  {
    title: 'The Story of Our Universe',
    prompt: 'Create an awe-inspiring cosmic education story about the beginning of the universe. Start with the Big Bang - a tiny point exploding into space, stars, and galaxies. Show stars being born, planets forming around our sun, and Earth appearing. Use dramatic, beautiful space illustrations. Text is narrative and poetic: "Long, long ago, there was nothing. Then, BANG! The universe exploded into being! Stars sparkled to life. Galaxies swirled. Our sun was born, and around it, planets danced. One special planet: Earth." Make it wonder-filled.',
    ageCategory: '7-8',
    difficultyLevel: 3,
    learningTopic: 'science',
    pageCount: 16,
    learningObjectives: [
      'Introduce the Big Bang and universe formation',
      'Learn space vocabulary (galaxy, star, planet, universe) in both languages',
      'Inspire wonder and curiosity about cosmology',
      'Understand Earth\'s place in the universe'
    ],
    montessoriPrinciples: [
      'First Great Lesson: Coming of the Universe',
      'Cosmic education sparks imagination',
      'Gratitude for the cosmic events that created our world'
    ],
    sensitivePeriods: ['language'],
    parentGuidance: 'This is the first of Montessori\'s Great Lessons. After reading, look at stars together. Visit a planetarium if possible. Discuss: how old is the universe? How did Earth form? Encourage big questions. This story is meant to inspire research.',
    followUpActivities: [
      'Visit a planetarium or watch space documentaries',
      'Make a model solar system',
      'Research how stars form',
      'Stargaze and identify constellations'
    ]
  },
  {
    title: 'The Great Inventors: Thomas Edison',
    prompt: 'Create a biographical story about Thomas Edison as a child and adult. Show young Edison asking questions, experimenting, failing many times, and finally inventing the light bulb. Include his famous quote about not failing but finding 10,000 ways that won\'t work. Use historical illustrations. Text: "When Thomas was young, he asked a lot of questions. He loved to experiment. As a grown-up, he wanted to make a light that would last. He tried again and again. After many tries, he succeeded! The light bulb lit up the world." Emphasize perseverance.',
    ageCategory: '7-8',
    difficultyLevel: 3,
    learningTopic: 'history',
    pageCount: 14,
    learningObjectives: [
      'Learn about Thomas Edison and his contributions',
      'Understand the value of perseverance and failure',
      'Build historical biography comprehension',
      'Inspire curiosity and invention'
    ],
    montessoriPrinciples: [
      'Cosmic education: humans as inventors and problem-solvers',
      'Gratitude for contributions of individuals',
      'Learning from failure is part of growth'
    ],
    sensitivePeriods: ['language'],
    parentGuidance: 'Discuss: "What would life be like without electric lights?" Encourage your child\'s own experiments and inventions. Celebrate mistakes as learning. You can try simple science experiments together. Montessori values "going out" - visit a science museum.',
    followUpActivities: [
      'Conduct simple electricity experiments (with supervision)',
      'Invent something of your own (drawing or prototype)',
      'Visit a science or technology museum',
      'Read about other inventors'
    ]
  },
  {
    title: 'The Tortoise and the Hare: A Fable',
    prompt: 'Create a bilingual retelling of Aesop\'s fable "The Tortoise and the Hare." A speedy hare challenges a slow tortoise to a race. The hare is overconfident and takes a nap, while the tortoise keeps going steadily. The tortoise wins! Use beautiful storybook illustrations. Text alternates perspectives: the hare\'s boasting, the tortoise\'s determination, the race, the surprise ending. End with the moral: "Slow and steady wins the race." Make it engaging and clear.',
    ageCategory: '7-8',
    difficultyLevel: 2,
    learningTopic: 'cultural',
    pageCount: 12,
    learningObjectives: [
      'Understand fables and morals',
      'Learn story elements (characters, plot, lesson)',
      'Build reading comprehension for literary forms',
      'Discuss values like perseverance and humility'
    ],
    montessoriPrinciples: [
      'Cultural literacy through classic tales',
      'Morals and values are explored, not imposed',
      'Fables are universal and translate well across cultures'
    ],
    sensitivePeriods: ['language', 'social'],
    parentGuidance: 'After reading, discuss: "What is the moral? Do you agree? When is it good to go slow? When is speed helpful?" Relate to your child\'s life: homework, learning a skill. Fables are great for critical thinking discussions.',
    followUpActivities: [
      'Act out the fable with family or toys',
      'Write your own fable with animal characters and a moral',
      'Read other Aesop\'s fables',
      'Discuss real-life situations where the moral applies'
    ]
  },
  {
    title: 'Volcanoes: Earth\'s Fiery Mountains',
    prompt: 'Create an educational science story explaining volcanoes. Show Earth\'s layers, magma beneath the surface, how pressure builds, and an eruption. Include types: shield, composite, cinder cone. Use dramatic, accurate illustrations. Text: "Deep inside Earth, it is very hot. Melted rock called magma swirls. Sometimes, pressure builds. The magma pushes up, up, up... BOOM! A volcano erupts! Lava flows down the mountain. Ash fills the sky." Explain it\'s a natural process and name famous volcanoes.',
    ageCategory: '7-8',
    difficultyLevel: 4,
    learningTopic: 'science',
    pageCount: 16,
    learningObjectives: [
      'Understand how volcanoes form and erupt',
      'Learn geology vocabulary (magma, lava, crust, mantle, eruption) in both languages',
      'Identify different volcano types',
      'Appreciate Earth\'s dynamic processes'
    ],
    montessoriPrinciples: [
      'Cosmic education: understanding Earth\'s formation',
      'Science through storytelling and drama',
      'Real phenomena inspire awe and respect for nature'
    ],
    sensitivePeriods: ['language'],
    parentGuidance: 'After reading, demonstrate a volcano eruption using baking soda and vinegar (classic science experiment). Look up videos of real eruptions. Locate volcanoes on a map. Discuss: "Why do people live near volcanoes? Are they dangerous?" Encourage research.',
    followUpActivities: [
      'Make a baking soda and vinegar volcano',
      'Research famous volcanoes (Vesuvius, Krakatoa, Mount St. Helens)',
      'Find the "Ring of Fire" on a world map',
      'Learn about benefits of volcanic soil'
    ]
  },
  {
    title: 'A Day in the Life: Japan',
    prompt: 'Create a detailed cultural story following a child named Hana through a typical day in Japan. Show her morning (bowing, breakfast of rice and fish), going to school (removing shoes, group cleaning time), eating bento lunch, after-school calligraphy class, and evening family time. Use authentic details and respectful illustrations. Text: "Hana wakes up in Tokyo. She bows and says "Ohayou gozaimasu" - Good morning! At school, everyone takes off their shoes. At lunch, Hana opens her bento box..." Make it informative and engaging.',
    ageCategory: '7-8',
    difficultyLevel: 3,
    learningTopic: 'cultural',
    pageCount: 16,
    learningObjectives: [
      'Learn about daily life in Japan',
      'Build cultural vocabulary and phrases',
      'Understand similarities and differences across cultures',
      'Develop global perspective'
    ],
    montessoriPrinciples: [
      'Cultural geography and studies',
      'Respect and curiosity for other cultures',
      'Cosmic education: we are all part of the human family'
    ],
    sensitivePeriods: ['language', 'social'],
    parentGuidance: 'Find Japan on a map. Learn a few Japanese phrases together. Try making onigiri (rice balls) or origami. Discuss: "How is Hana\'s day similar to yours? How is it different?" This builds cultural awareness and respect.',
    followUpActivities: [
      'Learn basic Japanese greetings',
      'Try Japanese food or cook onigiri',
      'Practice origami',
      'Learn about Japanese festivals and traditions'
    ]
  }
];

/**
 * AGE 9-10: UPPER ELEMENTARY
 * Focus: Advanced cosmic education, research, classic literature, independent learning
 * Montessori: Abstract thinking, moral development, cosmic task, going out
 */
export const AGE_9_10_PROMPTS: LearningPrompt[] = [
  {
    title: 'The Ancient Maya Civilization',
    prompt: 'Create an in-depth historical exploration of the Maya civilization. Cover their achievements: advanced calendar, writing system (hieroglyphs), mathematics (concept of zero), pyramids, astronomy. Show daily life, cities like Tikal, and eventual decline. Use accurate historical illustrations and photographs of ruins. Text: "Long ago in Central America, the Maya people built a great civilization. They created pyramids that touched the sky. They invented a complex calendar and a writing system. They understood the stars..." Make it factual, engaging, and respectful.',
    ageCategory: '9-10',
    difficultyLevel: 4,
    learningTopic: 'history',
    pageCount: 20,
    learningObjectives: [
      'Understand Maya civilization\'s contributions',
      'Learn historical and archaeological vocabulary in both languages',
      'Appreciate ancient innovations',
      'Develop research and critical thinking skills'
    ],
    montessoriPrinciples: [
      'Cosmic education: Timeline of Humans',
      'Gratitude for contributions of past civilizations',
      'History connects us to our human story'
    ],
    sensitivePeriods: ['language'],
    parentGuidance: 'Use a map to show where the Maya lived. If possible, visit a museum with Maya artifacts. Encourage independent research: your child can learn more about Maya food, games, or art. Montessori encourages "going out" to libraries and museums for deeper learning.',
    followUpActivities: [
      'Create a Maya codex (folded book) with hieroglyphs',
      'Research Maya number system and try writing numbers',
      'Make a model pyramid',
      'Watch documentaries about Maya ruins'
    ]
  },
  {
    title: 'The Story of Writing: From Pictures to Letters',
    prompt: 'Create a fascinating historical narrative about how writing evolved. Start with cave paintings, move to ancient Sumerian cuneiform (wedge marks on clay), Egyptian hieroglyphs, Phoenician alphabet, and eventually modern alphabets. Show how writing changed civilization: recording history, laws, stories. Use timeline format with illustrations of each writing system. Text: "Long ago, humans drew pictures on cave walls to tell stories. Then, in Mesopotamia, people pressed symbols into clay - the first writing! Egyptians drew hieroglyphs... Eventually, the alphabet we use today was born." Make it a journey through time.',
    ageCategory: '9-10',
    difficultyLevel: 4,
    learningTopic: 'history',
    pageCount: 18,
    learningObjectives: [
      'Understand the evolution of writing systems',
      'Appreciate the importance of written language',
      'Learn historical vocabulary (cuneiform, hieroglyphs, alphabet)',
      'Connect to bilingual learning - many writing systems exist'
    ],
    montessoriPrinciples: [
      'Timeline of Humans: development of language and writing',
      'Cosmic education: humans as communicators',
      'Gratitude for the gift of writing'
    ],
    sensitivePeriods: ['language'],
    parentGuidance: 'After reading, try writing in different systems: draw a picture-story (cave painting style), create hieroglyphs, or write in a different alphabet (Greek, Arabic, etc.). Discuss: "Why was writing so important? What would life be like without it?" This deepens appreciation for literacy.',
    followUpActivities: [
      'Create your own pictograph language',
      'Try writing your name in hieroglyphs',
      'Research how your target language\'s alphabet developed',
      'Visit a museum to see ancient writing artifacts'
    ]
  },
  {
    title: 'The Little Prince: Adapted Chapter',
    prompt: 'Create an adapted bilingual version of a chapter from "The Little Prince" by Antoine de Saint-Exupéry. Choose a meaningful chapter, like meeting the fox ("One only understands the things that one tames..."). Use poetic, gentle language and whimsical illustrations that echo the original. Text in parallel languages, preserving the philosophical beauty: "It is only with the heart that one can see rightly; what is essential is invisible to the eye." Keep the wonder and depth accessible to children.',
    ageCategory: '9-10',
    difficultyLevel: 5,
    learningTopic: 'language',
    pageCount: 16,
    learningObjectives: [
      'Experience classic literature in two languages',
      'Understand deeper themes (friendship, love, responsibility)',
      'Build advanced reading comprehension',
      'Appreciate literary language and metaphor'
    ],
    montessoriPrinciples: [
      'Cultural literacy through world literature',
      'Respecting the child\'s capacity for philosophy',
      'Beauty and meaning in language'
    ],
    sensitivePeriods: ['language'],
    parentGuidance: 'This is a more advanced text. Read together and discuss: "What does the fox mean about taming? What is essential but invisible?" Montessori believes children can handle deep ideas. Encourage your child to illustrate their own interpretation of the chapter.',
    followUpActivities: [
      'Read the full "Little Prince" book',
      'Draw your own planet or character',
      'Discuss the book\'s themes in family conversations',
      'Write a letter to the Little Prince'
    ]
  },
  {
    title: 'How Volcanoes Shape Our World',
    prompt: 'Create an advanced science exploration of volcanoes and their global impact. Go beyond eruptions: explain volcanic islands (Hawaii), fertile soil (why people farm near volcanoes), geothermal energy (Iceland), and volcanic winter (historical eruptions that affected climate). Use maps, diagrams, and photos. Text is informational and analytical: "Volcanoes are not just dangerous - they are creators! The Hawaiian Islands were built by underwater volcanoes. Volcanic ash makes soil rich for farming. In Iceland, people use volcanic heat for energy. But big eruptions can cool the whole Earth..." Encourage critical thinking.',
    ageCategory: '9-10',
    difficultyLevel: 5,
    learningTopic: 'science',
    pageCount: 20,
    learningObjectives: [
      'Understand complex Earth science concepts',
      'Learn advanced geology vocabulary in both languages',
      'Appreciate interconnections (volcanoes affect climate, farming, energy)',
      'Develop analytical thinking: costs and benefits'
    ],
    montessoriPrinciples: [
      'Cosmic education: everything is interconnected',
      'Science explores complexity, not just simple facts',
      'Real-world applications make learning meaningful'
    ],
    sensitivePeriods: ['language'],
    parentGuidance: 'After reading, research a specific volcano or volcanic region. Encourage your child to create a presentation or written report. Montessori upper elementary students often do independent projects. Support their curiosity and help them find resources.',
    followUpActivities: [
      'Research geothermal energy use in Iceland',
      'Map volcanic islands worldwide',
      'Investigate the 1815 Tambora eruption and "year without summer"',
      'Create a poster: "Benefits and Dangers of Volcanoes"'
    ]
  },
  {
    title: 'Young Activists: Greta Thunberg\'s Story',
    prompt: 'Create an inspiring biographical story about Greta Thunberg, the young climate activist. Start with her learning about climate change in school, feeling worried, deciding to act (school strike), speaking at the UN, and inspiring millions of young people worldwide. Use real photos and respectful narrative. Text: "When Greta was 15, she learned Earth\'s climate was changing. She felt scared. But instead of giving up, she decided to act. She sat outside her country\'s parliament with a sign: \'School Strike for Climate.\' Soon, millions of young people joined her..." Emphasize youth empowerment and taking action.',
    ageCategory: '9-10',
    difficultyLevel: 4,
    learningTopic: 'social-emotional',
    pageCount: 18,
    learningObjectives: [
      'Learn about modern activism and climate change',
      'Understand one person can make a difference',
      'Build vocabulary for environmental and social action',
      'Inspire civic engagement and responsibility'
    ],
    montessoriPrinciples: [
      'Cosmic task: each person has a role in caring for Earth',
      'Peace education includes environmental stewardship',
      'Children are capable of meaningful action'
    ],
    sensitivePeriods: ['language', 'social'],
    parentGuidance: 'Discuss: "What issue do you care about? How can you make a difference?" Montessori education culminates in the child understanding their cosmic task - their unique contribution to the world. Support your child in finding their voice and taking age-appropriate action (writing letters, community projects).',
    followUpActivities: [
      'Research climate change and its effects',
      'Start a small environmental project (recycling, planting trees)',
      'Write a letter to a leader about an issue',
      'Learn about other young activists'
    ]
  }
];

/**
 * Get all prompts for a specific age category
 */
export function getPromptsByAge(ageCategory: AgeCategory): LearningPrompt[] {
  const promptMap: Record<AgeCategory, LearningPrompt[]> = {
    '0-1': AGE_0_1_PROMPTS,
    '1-2': AGE_1_2_PROMPTS,
    '3-4': AGE_3_4_PROMPTS,
    '5-6': AGE_5_6_PROMPTS,
    '7-8': AGE_7_8_PROMPTS,
    '9-10': AGE_9_10_PROMPTS,
  };
  return promptMap[ageCategory] || [];
}

/**
 * Get all prompts
 */
export function getAllLearningPrompts(): LearningPrompt[] {
  return [
    ...AGE_0_1_PROMPTS,
    ...AGE_1_2_PROMPTS,
    ...AGE_3_4_PROMPTS,
    ...AGE_5_6_PROMPTS,
    ...AGE_7_8_PROMPTS,
    ...AGE_9_10_PROMPTS,
  ];
}

/**
 * Get prompts filtered by topic
 */
export function getPromptsByTopic(topic: LearningTopic): LearningPrompt[] {
  return getAllLearningPrompts().filter((p) => p.learningTopic === topic);
}

/**
 * Get prompts filtered by difficulty level
 */
export function getPromptsByLevel(level: number): LearningPrompt[] {
  return getAllLearningPrompts().filter((p) => p.difficultyLevel === level);
}

/**
 * Get age-appropriate Montessori metadata
 */
export function getAgeMetadata(ageCategory: AgeCategory) {
  const metadata: Record<AgeCategory, {
    title: string;
    description: string;
    icon: string;
    sensitivePeriods: string[];
    developmentalFocus: string[];
    montessoriMaterials: string[];
  }> = {
    '0-1': {
      title: 'Infants (0-1 years)',
      description: 'Sensory exposure, first words, and loving bonds',
      icon: 'Baby',
      sensitivePeriods: ['Language absorption begins', 'Sensorial exploration', 'Order and routine'],
      developmentalFocus: ['Absorbing sounds of both languages', 'Visual and auditory stimulation', 'Bonding through reading'],
      montessoriMaterials: ['Mobiles', 'Picture cards', 'High-contrast images', 'Soft books']
    },
    '1-2': {
      title: 'Toddlers (1-2 years)',
      description: 'Action words, daily routines, and first phrases',
      icon: 'Footprints',
      sensitivePeriods: ['Language explosion', 'Movement coordination', 'Order and sequence'],
      developmentalFocus: ['Vocabulary growth', 'Action verbs', 'Independence in practical life'],
      montessoriMaterials: ['Practical life activities', 'Action cards', 'Simple puzzles', 'Dressing frames']
    },
    '3-4': {
      title: 'Preschool (3-4 years)',
      description: 'Stories, nature, cultural awareness, and social skills',
      icon: 'Sprout',
      sensitivePeriods: ['Language refinement', 'Sensorial discrimination', 'Social awareness'],
      developmentalFocus: ['Narrative comprehension', 'Sensorial exploration', 'Grace and courtesy'],
      montessoriMaterials: ['Pink tower', 'Color tablets', 'Practical life trays', 'Geography puzzle maps']
    },
    '5-6': {
      title: 'Kindergarten (5-6 years)',
      description: 'Early reading, science exploration, and cultural stories',
      icon: 'BookOpen',
      sensitivePeriods: ['Reading/writing emergence', 'Cultural curiosity', 'Moral development'],
      developmentalFocus: ['Phonetic reading', 'Science concepts', 'Cultural literacy'],
      montessoriMaterials: ['Moveable alphabet', 'Science experiments', 'Cultural materials', 'Timeline activities']
    },
    '7-8': {
      title: 'Lower Elementary (7-8 years)',
      description: 'Cosmic education, biographies, and deeper science',
      icon: 'Globe2',
      sensitivePeriods: ['Imagination and reasoning', 'Social collaboration', 'Moral reasoning'],
      developmentalFocus: ['Cosmic education stories', 'Research skills', 'Abstract thinking'],
      montessoriMaterials: ['Great Lessons', 'Timelines', 'Science experiments', 'Research materials']
    },
    '9-10': {
      title: 'Upper Elementary (9-10 years)',
      description: 'Independent research, classic literature, and global awareness',
      icon: 'Microscope',
      sensitivePeriods: ['Abstract reasoning', 'Justice and fairness', 'Cosmic task awareness'],
      developmentalFocus: ['Independent study', 'Creative expression', 'Social responsibility'],
      montessoriMaterials: ['Research projects', 'Advanced timelines', 'Literature studies', 'Community work']
    }
  };
  
  return metadata[ageCategory];
}

